import { createClient } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { readSupabaseOverride } from "@/lib/chatbotBaseUrl";
import {
  getBuildingStorageQuota,
  parseBuildingStorageId,
} from "@/domains/documents/services/buildingStorageQuota";
import {
  accountProjectStorage,
  storageBytes,
  type BuildingUsage,
  type UsageDocument,
  type UsageProject,
} from "./storageAccounting";

import { storageRead } from "./storageReadQueue";
import { storageInfoFetch } from "./storageInfoFetch";

const PAGE_SIZE = 1000;
export async function readAllUsagePages<T>(
  read: (offset: number) => PromiseLike<T[]>,
  signal: AbortSignal
): Promise<T[]> {
  const rows: T[] = [];
  for (let offset = 0; ; offset += PAGE_SIZE) {
    signal.throwIfAborted();
    const page = await read(offset);
    signal.throwIfAborted();
    rows.push(...page);
    if (page.length < PAGE_SIZE) return rows;
  }
}

export async function getUsageBuildings(orgId: string, signal: AbortSignal) {
  if (!orgId || orgId.trim() !== orgId) throw new Error("Invalid organisation");
  return readAllUsagePages<{ id: number; name: string }>(async (offset) => {
    const { data, error } = await supabase
      .from("buildings")
      .select("id,name")
      .eq("org_id", orgId)
      .order("id")
      .range(offset, offset + PAGE_SIZE - 1)
      .abortSignal(signal);
    if (error) throw error;
    return data ?? [];
  }, signal);
}

// A separate read client uses the existing session and endpoint, with cancellation
// even for Storage.info (whose installed SDK signature has no signal parameter).
function storageReadClient(signal: AbortSignal) {
  const override = readSupabaseOverride();
  const endpoint = override?.url ?? import.meta.env.VITE_SUPABASE_URL;
  return createClient<Database>(
    endpoint,
    override?.anonKey ?? import.meta.env.VITE_SUPABASE_ANON_KEY,
    {
      accessToken: async () => {
        signal.throwIfAborted();
        const { data, error } = await supabase.auth.getSession();
        if (error || !data.session) throw new Error("Authentication required");
        return data.session.access_token;
      },
      global: { fetch: storageInfoFetch(endpoint, signal) },
    }
  );
}

function readUsageProjects(orgId: string, buildingId: number, signal: AbortSignal) {
  return readAllUsagePages<UsageProject>(async (offset) => {
    const result = await supabase
      .from("building_projects")
      .select("id,name")
      .eq("org_id", orgId)
      .eq("building_id", buildingId)
      .eq("hidden", false)
      .eq("archived", false)
      .order("id")
      .range(offset, offset + PAGE_SIZE - 1)
      .abortSignal(signal);
    if (result.error) throw result.error;
    return result.data ?? [];
  }, signal);
}

function readUsageDocuments(buildingId: number, signal: AbortSignal) {
  return readAllUsagePages<UsageDocument>(async (offset) => {
    const result = await supabase
      .from("documents")
      .select("file_path,size,project_id")
      .eq("building_id", buildingId)
      .eq("is_deleted", false)
      .eq("storage_quota_counted", true)
      .not("file_path", "is", null)
      .order("id")
      .range(offset, offset + PAGE_SIZE - 1)
      .abortSignal(signal);
    if (result.error) throw result.error;
    return result.data ?? [];
  }, signal);
}

async function originalStorageSizes(documents: UsageDocument[], signal: AbortSignal) {
  const client = storageReadClient(signal);
  const paths = [
    ...new Set(
      documents.flatMap((document) => (document.file_path === null ? [] : [document.file_path]))
    ),
  ];
  const metadata = new Map<string, number>();
  let next = 0;
  await Promise.all(
    Array.from({ length: Math.min(6, paths.length) }, async () => {
      while (next < paths.length) {
        signal.throwIfAborted();
        const path = paths[next++];
        const result = await storageRead(signal, () => client.storage.from("documents").info(path));
        signal.throwIfAborted();
        if (result.error) {
          if ("statusCode" in result.error && String(result.error.statusCode) === "404") continue;
          throw result.error;
        }
        const bytes = storageBytes(result.data.size);
        if (bytes !== null) metadata.set(path, bytes);
      }
    })
  );
  return metadata;
}

export async function getBuildingUsage(
  orgId: string,
  buildingId: number,
  signal: AbortSignal
): Promise<BuildingUsage> {
  if (parseBuildingStorageId(buildingId) === null) throw new Error("Invalid building");
  const { data: building, error } = await supabase
    .from("buildings")
    .select("id,name")
    .eq("org_id", orgId)
    .eq("id", buildingId)
    .abortSignal(signal)
    .single();
  if (error || !building) throw new Error("Building unavailable");
  const [quota, projects, documents] = await Promise.all([
    getBuildingStorageQuota(buildingId, signal),
    readUsageProjects(orgId, buildingId, signal),
    readUsageDocuments(buildingId, signal),
  ]);
  const metadata = await originalStorageSizes(documents, signal);
  // Authoritative totals win. Concurrent writes can invalidate the snapshot;
  // never redistribute a difference proportionally across projects.
  const currentQuota = await getBuildingStorageQuota(buildingId, signal);
  if (quota.usedBytes !== currentQuota.usedBytes || quota.limitBytes !== currentQuota.limitBytes) {
    throw new Error("Storage changed while loading. Please refresh.");
  }
  return {
    ...building,
    quota: currentQuota,
    ...accountProjectStorage(documents, projects, metadata, currentQuota.usedBytes),
  };
}
